import { Box, Button, Checkbox, CircularProgress, Dialog, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { parseTemplateWidgets, stripWidgetSettings } from './widgetTemplateParse'
import { serializeTemplateWidgets } from './widgetTemplateUtils'

const HEADER_HEIGHT = '3.5rem'

/**
 * Shows the SELECTED DEVICE's current widget layout, opened from the "Change
 * widget template" modal. It starts read-only (preview); the grid's own pencil
 * enters edit mode, and the grid's Save writes the widgets back TO THE DEVICE
 * (POST widgets/device/{id}) — this does NOT create a template, it just saves
 * the asset's layout at that moment. Additionally "Save as new template" stores
 * the layout currently shown (including edits) as a new template (HU-04).
 * onClose() closes; onSaved() is called after a template is created.
 */
const PreviewCurrentLayoutDialog = ({ open, device, onClose, onSaved }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [widgets, setWidgets] = useState([])
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [saveAsOpen, setSaveAsOpen] = useState(false)
  const [saveAsName, setSaveAsName] = useState('')
  const [copySettings, setCopySettings] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !device?.id) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const result = await apiGet('widgets/device/' + device.id)
        if (!cancelled) setWidgets(parseTemplateWidgets(Array.isArray(result) ? result : []))
      } catch (e) {
        if (!cancelled) showAlert({ title: t_('error loading widgets'), message: t_(e.message || 'Unknown error'), severity: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, device, reloadToken, showAlert, t_])

  const reloadWidgets = useCallback(() => setReloadToken((token) => token + 1), [])

  // Grid "Save": persists the edited layout to the DEVICE (not a template).
  const onSave = useCallback(async () => {
    if (!device) return
    try {
      await apiPost('widgets/device/' + device.id, widgets.map((item) => ({ ...item, data: item.data ? JSON.stringify(item.data) : null })))
      showAlert({ title: t_('Saved correctly'), message: '', severity: 'success' })
      setEditing(false)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    }
  }, [device, widgets, showAlert, t_])

  const doSaveAs = async () => {
    const name = saveAsName.trim()
    if (!name) {
      showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
      return
    }
    setSaveAsOpen(false)
    setBusy(true)
    try {
      await apiPost('widgets/templates', { name, widgets: serializeTemplateWidgets(copySettings ? widgets : stripWidgetSettings(widgets)) })
      showAlert({ title: t_('Template created'), message: '', severity: 'success' })
      if (onSaved) onSaved()
      onClose()
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const hasLayout = Array.isArray(widgets) && widgets.length > 0

  return (
    <Dialog fullScreen open={open} onClose={() => onClose()} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose()} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Preview current layout')}</Typography>
        <Typography color='text.secondary' noWrap sx={{ flexGrow: 1 }}>{device ? device.name : ''}</Typography>
        <Button
          variant='outlined'
          startIcon={<SaveIcon />}
          onClick={() => { setSaveAsName(''); setCopySettings(true); setSaveAsOpen(true) }}
          disabled={busy || !hasLayout}
        >
          {t_('Save as new template')}
        </Button>
      </Stack>
      <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            {loading
              ? <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}><CircularProgress size='6rem' /></Stack>
              : (
                <MosInfoProvider>
                  <WidgetGrid
                    widgets={widgets}
                    setWidgets={setWidgets}
                    reloadWidgets={reloadWidgets}
                    onSave={onSave}
                    editing={editing}
                    setEditing={setEditing}
                    showAllWidgets
                    toolbarLabel={device?.name}
                  />
                </MosInfoProvider>
                )}
          </Box>
        </Stack>
      </Box>

      <SmartDialog
        setOpen={saveAsOpen}
        title={createDialogTitle({ color: 'primary', title: t_('Save template'), icon: SaveIcon })}
        accept
        acceptLabel={t_('Save')}
        acceptCallback={doSaveAs}
        cancel
        cancelCallback={() => setSaveAsOpen(false)}
        renderComponent={
          <Box sx={{ p: 1, minWidth: '24rem' }}>
            <TextField
              autoFocus
              fullWidth
              label={t_('New template name')}
              value={saveAsName}
              onChange={(event) => setSaveAsName(event.target.value)}
              inputProps={{ maxLength: 255 }}
            />
            <FormControlLabel
              control={<Checkbox checked={copySettings} onChange={(event) => setCopySettings(event.target.checked)} size='small' />}
              label={t_('Copy widget settings')}
            />
          </Box>
        }
      />
    </Dialog>
  )
}

export default PreviewCurrentLayoutDialog
